import { z } from 'zod';
/**
 * Bookmark — a per-user, per-org pointer to an entity (customer, person,
 * topic, product, generic graph entity) that the CEO has flagged for the
 * agent to keep watching. Bookmarked items become "living dossiers" the
 * agent surfaces in briefings and the right-rail watchlist tab.
 *
 * Per-user (not per-org): two execs in the same org curate independent
 * watchlists. Storage doc id is composed `{userId}_{orgId}_{refId}` so a
 * single doc covers the (user, org, target) triple without a query.
 */
export const BOOKMARKS_COLLECTION = 'bookmarks';
export const BookmarkType = z.enum(['customer', 'person', 'topic', 'product', 'entity']);
export const Bookmark = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    userId: z.string().min(1),
    type: BookmarkType,
    /** Dossier id / person id / topic slug / entity id this bookmark targets. */
    refId: z.string().min(1),
    name: z.string().min(1),
    /** Optional one-line caption (e.g. "Series-B customer · $48k MRR"). */
    sub: z.string().optional(),
    tags: z.array(z.string()).default([]),
    /**
     * Pinned bookmarks float to the top of the Dossiers page. Used by the
     * onboarding seeder to surface the company dossier on day-1; users can
     * pin/unpin manually via the bookmark drawer. Keep separate from `tags`
     * — tags are user-facing labels, `pinned` is system sort state.
     *
     * Optional in the type (undefined on legacy rows that predate this
     * field); consumers should read `bookmark.pinned ?? false` for sort.
     */
    pinned: z.boolean().optional(),
    createdAt: z.string(),
});
/**
 * Composite doc id: `{userId}_{orgId}_{refId}`. Stable enough to dedupe
 * "bookmark this twice" attempts without a query, scoped enough to keep
 * cross-user / cross-org collisions impossible. The `_` separator is
 * safe because Firestore doc ids exclude `/`; user uids and org slugs
 * never contain `_` in practice (Firebase uids are alphanumeric, our
 * org ids are slugs).
 */
export function bookmarkDocId(userId, orgId, refId) {
    return `${userId}_${orgId}_${refId}`;
}
/** Body for `POST /api/orgs/:o/bookmarks`. Server stamps id + userId + createdAt. */
export const CreateBookmarkRequest = z.object({
    type: BookmarkType,
    refId: z.string().min(1),
    name: z.string().min(1),
    sub: z.string().optional(),
    tags: z.array(z.string()).max(32).default([]),
    pinned: z.boolean().optional(),
});
/** Wire shape returned by `GET /api/orgs/:o/bookmarks`. */
export const ListBookmarksResponse = z.object({
    bookmarks: z.array(Bookmark),
});
/**
 * Knowledge-graph wire shape returned by `GET /api/orgs/:o/bookmarks/graph`.
 * Until epic #9 (Neo4j) lands, the server returns synthetic edges
 * derived from co-bookmark patterns and shared tags.
 */
export const BookmarkGraphNode = z.object({
    refId: z.string().min(1),
    type: BookmarkType,
    name: z.string().min(1),
    sub: z.string().optional(),
});
export const BookmarkGraphEdge = z.object({
    from: z.string().min(1),
    to: z.string().min(1),
    label: z.string().optional(),
});
export const BookmarkGraphResponse = z.object({
    nodes: z.array(BookmarkGraphNode),
    edges: z.array(BookmarkGraphEdge),
});
